import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { IMG, NAV_ITEMS, TOP_BAR } from '../mock/mock';
import { LeafTopLeft, LeafTopRight } from './Leaves';

export default function CTASection() {
  return (
    <section id="kapcsolat" className="relative bg-[#0a1d2e]">
      {/* CTA on image */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.sunset} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-[#0a1d2e]" />
        </div>

        {/* Leaf corners */}
        <LeafTopLeft className="absolute top-0 left-0 w-40 md:w-52 h-auto pointer-events-none z-[5]" />
        <LeafTopRight className="absolute top-0 right-0 w-40 md:w-52 h-auto pointer-events-none z-[5]" />

        {/* faint house outline right */}
        <div aria-hidden className="absolute right-6 top-1/2 -translate-y-1/2 w-[260px] h-[200px] opacity-25 pointer-events-none">
          <svg viewBox="0 0 400 300" className="w-full h-full text-white">
            <path d="M50 250 L50 130 L200 30 L350 130 L350 250 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <rect x="170" y="160" width="60" height="90" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <rect x="90" y="170" width="50" height="50" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <rect x="260" y="170" width="50" height="50" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </div>

        <div className="relative max-w-3xl mx-auto px-6 py-32 md:py-40 text-center">
          <div className="flex justify-center mb-7">
            <div className="w-12 h-px bg-[#2BA8C0]" />
          </div>
          <p className="text-white/85 text-[12px] md:text-[13px] uppercase tracking-[0.35em] mb-7">
            Az Ön álma, a Velencei-tó környékén
          </p>
          <h2 className="font-serif-display text-white text-5xl md:text-7xl leading-[1.05] mb-7">
            Építsük fel <span className="italic text-[#2BA8C0]">közösen.</span>
          </h2>
          <p className="text-white/85 text-[16px] md:text-[18px] leading-relaxed max-w-xl mx-auto mb-10">
            Lépjen velünk kapcsolatba, és tervezzük meg együtt a vízparti otthonát.
          </p>
          <a
            href="#survey"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#2BA8C0] text-white text-[14px] font-medium hover:bg-[#239ab1] transition-colors shadow-lg group"
          >
            Álomház konfigurátor
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0a1d2e] text-white/80">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
          <div className="grid md:grid-cols-12 gap-10 pb-10">
            <div className="md:col-span-4">
              <div className="flex items-center gap-2.5 mb-5">
                <img src={IMG.logo} alt="" className="w-9 h-9 object-contain" />
                <span className="text-white text-lg font-medium tracking-tight">Eco-Lak Kft.</span>
              </div>
              <p className="text-white/55 text-[14px] leading-relaxed max-w-sm">
                Fenntartható könnyűszerkezetes készházak a Velencei-tó partján. Modern technológia,
                személyre szabott design.
              </p>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-white/85 text-[12px] uppercase tracking-[0.25em] font-semibold mb-5">Kapcsolat</h4>
              <ul className="space-y-3 text-[14px]">
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#2BA8C0]/60" />
                  <a href={`tel:${TOP_BAR.phone}`} className="text-white/40 hover:text-white/70 transition-colors">{TOP_BAR.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#2BA8C0]/60" />
                  <a href={`mailto:${TOP_BAR.email}`} className="text-white/40 hover:text-white/70 transition-colors">{TOP_BAR.email}</a>
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#2BA8C0]/60" />
                  <span className="text-white/40">Gárdony, Velencei-tó</span>
                </li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-white text-[12px] uppercase tracking-[0.25em] font-semibold mb-5">Navigáció</h4>
              <ul className="space-y-2.5">
                <li><a href="#" className="text-white/65 hover:text-[#2BA8C0] text-[14px] transition-colors">Kezdőlap</a></li>
                {NAV_ITEMS.slice(0, 4).map((it) => (
                  <li key={it.label}>
                    <a href={it.href} className="text-white/65 hover:text-[#2BA8C0] text-[14px] transition-colors">{it.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3 md:text-right">
              <p className="text-white/55 text-[14px] leading-relaxed mb-3">
                © {new Date().getFullYear()} Eco-Lak Kft.<br />
                Velencei-tó, Fejér megye<br />
                Minden jog fenntartva.
              </p>
              <div className="flex md:justify-end gap-5 text-[13px] text-white/50 mt-5">
                <Link to="/cookie" className="hover:text-white transition-colors">Cookie szabályzat</Link>
                <Link to="/adatvedelem" className="hover:text-white transition-colors">Adatvédelem</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
}
